import {
  createContext,
  forwardRef,
  useContext,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { motion } from 'motion/react';
import { shake } from '../motion/presets';
import { useReducedMotion } from '../motion/useReducedMotion';
import { cx } from '../internal/cx';

type FieldContextValue = {
  inputId: string;
  describedBy?: string;
  invalid: boolean;
};

const FieldContext = createContext<FieldContextValue | null>(null);

export type FieldProps = {
  label: string;
  hint?: string;
  error?: string;
  /** Id de l'input (généré sinon). */
  id?: string;
  children: ReactNode;
  className?: string;
};

/**
 * Champ de formulaire : label lié, hint, erreur (`aria-invalid` +
 * `aria-describedby` posés sur l'`<Input>` enfant via contexte).
 * Secousse douce du contenu quand une erreur apparaît (off si reduced-motion).
 */
export function Field({ label, hint, error, id, children, className }: FieldProps) {
  const autoId = useId();
  const inputId = id ?? `field-${autoId}`;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-err`;
  const reduced = useReducedMotion();

  const describedBy =
    [error ? errorId : null, hint && !error ? hintId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cx('block', className)}>
      <label htmlFor={inputId} className="mb-1.5 block text-sm font-semibold text-ink-100">
        {label}
      </label>
      <FieldContext.Provider value={{ inputId, describedBy, invalid: Boolean(error) }}>
        <motion.div
          variants={shake}
          animate={error && !reduced ? 'shake' : 'still'}
          // La clé force une nouvelle secousse si le message d'erreur change.
          key={error ?? 'ok'}
        >
          {children}
        </motion.div>
      </FieldContext.Provider>
      {hint && !error && (
        <p id={hintId} className="mt-1.5 text-xs text-ink-400">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="mt-1.5 text-sm font-medium text-danger-500">
          {error}
        </p>
      )}
    </div>
  );
}

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  /** Force l'état invalide hors `<Field>`. */
  invalid?: boolean;
};

/**
 * Input stylé du design system (glow au focus). Sous `<Field>`, récupère
 * automatiquement id, `aria-invalid` et `aria-describedby`.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, className, id, ...rest },
  ref,
) {
  const field = useContext(FieldContext);
  const isInvalid = invalid ?? field?.invalid ?? false;
  return (
    <input
      ref={ref}
      id={id ?? field?.inputId}
      aria-invalid={isInvalid || undefined}
      aria-describedby={rest['aria-describedby'] ?? field?.describedBy}
      className={cx(
        'w-full min-h-[2.75rem] rounded-lg border bg-ink-950/40 px-4 py-2.5 text-ink-100',
        'placeholder:text-ink-400/70 outline-none transition-[border-color,box-shadow] duration-fast ease-fun',
        isInvalid
          ? 'border-danger-500 focus:shadow-[0_0_0_3px_rgb(var(--danger-500)/0.25)]'
          : 'border-ink-600 focus:border-panda-500 focus:shadow-[0_0_0_3px_rgb(var(--panda-500)/0.25)]',
        className,
      )}
      {...rest}
    />
  );
});

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  /** Force l'état invalide hors `<Field>`. */
  invalid?: boolean;
};

/** Textarea stylé du design system — mêmes liaisons `<Field>` que `<Input>`. */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, className, id, ...rest },
  ref,
) {
  const field = useContext(FieldContext);
  const isInvalid = invalid ?? field?.invalid ?? false;
  return (
    <textarea
      ref={ref}
      id={id ?? field?.inputId}
      aria-invalid={isInvalid || undefined}
      aria-describedby={rest['aria-describedby'] ?? field?.describedBy}
      className={cx(
        'min-h-[5.5rem] w-full rounded-lg border bg-ink-950/40 px-4 py-2.5 text-ink-100',
        'placeholder:text-ink-400/70 outline-none transition-[border-color,box-shadow] duration-fast ease-fun',
        isInvalid
          ? 'border-danger-500 focus:shadow-[0_0_0_3px_rgb(var(--danger-500)/0.25)]'
          : 'border-ink-600 focus:border-panda-500 focus:shadow-[0_0_0_3px_rgb(var(--panda-500)/0.25)]',
        className,
      )}
      {...rest}
    />
  );
});
